import { UserDetails } from '@interfaces/index';
import { UserStatusInterface } from '@api/userStatusApiHandler';
import {
  EMPLOYER_TRAUMA_INCIDENTS_ROUTE,
  EMPLOYER_TRAUMA_STAFF_DASHBOARD_ROUTE,
} from '@config/employerTraumaIncidentsContent';

export type AdminPermissionsSnapshot = Pick<
  UserDetails,
  | 'adminManageUsers'
  | 'adminManageAnalytics'
  | 'adminManageSubClientAnalytics'
  | 'adminManageCommentsAndSpeakUp'
  | 'adminManageFreedomToSpeakUp'
  | 'freedomToSpeakUpAlertEligibility'
  | 'adminManageCommunity'
  | 'adminReceiveCommunityAlerts'
  | 'adminManageTrauma'
  | 'adminLogTraumaIncidents'
  | 'adminViewTraumaIndividualRecords'
  | 'adminRespondToTraumaMessages'
  | 'adminUpdateTraumaOutcomes'
  | 'traumaAlertEligibility'
>;

const ADMIN_PERMISSIONS_STORAGE_KEY = 'adminPermissions';

/** Coerce API / DB booleans (true, 1, "1", "true"). */
export const coerceBoolean = (value: unknown): boolean =>
  value === true || value === 1 || value === '1' || value === 'true';

/** Unwrap GET /user/:id style payloads and normalise F2SU permission fields. */
export const normalizeUserApiRecord = <T extends Record<string, unknown>>(
  raw: T,
): T & {
  adminManageFreedomToSpeakUp: boolean;
  freedomToSpeakUpAlertEligibility: boolean;
  adminManageSubClientAnalytics: boolean;
  adminManageAnalytics: boolean;
  adminManageTrauma: boolean;
  adminLogTraumaIncidents: boolean;
  adminViewTraumaIndividualRecords: boolean;
  adminRespondToTraumaMessages: boolean;
  adminUpdateTraumaOutcomes: boolean;
  traumaAlertEligibility: boolean;
} => {
  const record = (raw?.responseObject ?? raw?.data ?? raw) as Record<string, unknown>;

  const adminManageFreedomToSpeakUp = coerceBoolean(
    record.adminManageFreedomToSpeakUp ??
      record.canManageFreedomToSpeakUp ??
      record.can_manage_freedom_to_speakup,
  );

  const freedomToSpeakUpAlertEligibility = coerceBoolean(
    record.freedomToSpeakUpAlertEligibility ??
      record.isEligibleForFreedomToSpeakupAlerts ??
      record.is_eligible_for_freedom_to_speakup_alerts,
  );

  const adminManageSubClientAnalytics = coerceBoolean(
    record.adminManageSubClientAnalytics ??
      record.canManageSubClientAnalytics ??
      record.can_manage_sub_client_analytics,
  );

  const adminManageAnalytics = coerceBoolean(
    record.adminManageAnalytics ?? record.canManageAnalytics ?? record.can_manage_analytics,
  );

  const adminManageTrauma = coerceBoolean(
    record.adminManageTrauma ?? record.canManageTrauma ?? record.can_manage_trauma,
  );

  const adminLogTraumaIncidents = coerceBoolean(
    record.adminLogTraumaIncidents ??
      record.canLogTraumaIncidents ??
      record.can_log_trauma_incidents,
  );

  const adminViewTraumaIndividualRecords = coerceBoolean(
    record.adminViewTraumaIndividualRecords ??
      record.canViewTraumaIndividualRecords ??
      record.can_view_trauma_individual_records,
  );

  const adminRespondToTraumaMessages = coerceBoolean(
    record.adminRespondToTraumaMessages ??
      record.canRespondToTraumaMessages ??
      record.can_respond_to_trauma_messages,
  );

  const adminUpdateTraumaOutcomes = coerceBoolean(
    record.adminUpdateTraumaOutcomes ??
      record.canUpdateTraumaOutcomes ??
      record.can_update_trauma_outcomes,
  );

  const traumaAlertEligibility = coerceBoolean(
    record.traumaAlertEligibility ??
      record.isEligibleForTraumaAlerts ??
      record.is_eligible_for_trauma_alerts,
  );

  return {
    ...(record as T),
    adminManageFreedomToSpeakUp,
    freedomToSpeakUpAlertEligibility,
    adminManageSubClientAnalytics,
    adminManageAnalytics,
    adminManageTrauma,
    adminLogTraumaIncidents,
    adminViewTraumaIndividualRecords,
    adminRespondToTraumaMessages,
    adminUpdateTraumaOutcomes,
    traumaAlertEligibility,
  };
};

/** Maps GET /get-user-status permission fields (canManage*) to adminPermissions shape. */
export const mapUserStatusToAdminPermissions = (
  status: UserStatusInterface | null | undefined,
): AdminPermissionsSnapshot => ({
  adminManageUsers: coerceBoolean(status?.canManageUsers),
  adminManageAnalytics: coerceBoolean(status?.canManageAnalytics),
  adminManageSubClientAnalytics: coerceBoolean(status?.canManageSubClientAnalytics),
  adminManageCommentsAndSpeakUp: coerceBoolean(status?.canManageCommentsAndSpeakup),
  adminManageFreedomToSpeakUp: coerceBoolean(status?.canManageFreedomToSpeakup),
  freedomToSpeakUpAlertEligibility: coerceBoolean(status?.isEligibleForFreedomToSpeakupAlerts),
  adminManageCommunity: coerceBoolean(status?.canManageCommunity),
  adminReceiveCommunityAlerts: coerceBoolean(status?.isEligibleForCommunityAlerts),
  adminManageTrauma: coerceBoolean(status?.canManageTrauma),
  adminLogTraumaIncidents: coerceBoolean(status?.canLogTraumaIncidents),
  adminViewTraumaIndividualRecords: coerceBoolean(status?.canViewTraumaIndividualRecords),
  adminRespondToTraumaMessages: coerceBoolean(status?.canRespondToTraumaMessages),
  adminUpdateTraumaOutcomes: coerceBoolean(status?.canUpdateTraumaOutcomes),
  traumaAlertEligibility: coerceBoolean(status?.isEligibleForTraumaAlerts),
});

export const syncAdminPermissionsFromUserStatus = (
  status: UserStatusInterface | null | undefined,
): void => {
  if (!status) {
    return;
  }

  localStorage.setItem(
    ADMIN_PERMISSIONS_STORAGE_KEY,
    JSON.stringify(mapUserStatusToAdminPermissions(status)),
  );
};

export const toAdminPermissionsSnapshot = (
  user: Partial<UserDetails> | null | undefined,
): AdminPermissionsSnapshot => ({
  adminManageUsers: coerceBoolean(user?.adminManageUsers),
  adminManageAnalytics: coerceBoolean(user?.adminManageAnalytics),
  adminManageSubClientAnalytics: coerceBoolean(
    user?.adminManageSubClientAnalytics ??
      (user as Record<string, unknown> | undefined)?.canManageSubClientAnalytics,
  ),
  adminManageCommentsAndSpeakUp: coerceBoolean(user?.adminManageCommentsAndSpeakUp),
  adminManageFreedomToSpeakUp: coerceBoolean(user?.adminManageFreedomToSpeakUp),
  freedomToSpeakUpAlertEligibility: coerceBoolean(user?.freedomToSpeakUpAlertEligibility),
  adminManageCommunity: coerceBoolean(user?.adminManageCommunity),
  adminReceiveCommunityAlerts: coerceBoolean(user?.adminReceiveCommunityAlerts),
  adminManageTrauma: coerceBoolean(user?.adminManageTrauma),
  adminLogTraumaIncidents: coerceBoolean(user?.adminLogTraumaIncidents),
  adminViewTraumaIndividualRecords: coerceBoolean(user?.adminViewTraumaIndividualRecords),
  adminRespondToTraumaMessages: coerceBoolean(user?.adminRespondToTraumaMessages),
  adminUpdateTraumaOutcomes: coerceBoolean(user?.adminUpdateTraumaOutcomes),
  traumaAlertEligibility: coerceBoolean(user?.traumaAlertEligibility),
});

export const isValidUserDetailsRecord = (data: unknown): data is Partial<UserDetails> =>
  typeof data === 'object' && data !== null && 'id' in data;

/** Team-scoped analytics only — not full company admin analytics. */
export const isSubClientAnalyticsOnly = (
  user?: Partial<AdminPermissionsSnapshot> | null,
): boolean =>
  coerceBoolean(user?.adminManageSubClientAnalytics) && !coerceBoolean(user?.adminManageAnalytics);

export const canAccessEmployerAnalytics = (
  user?: Partial<AdminPermissionsSnapshot> | null,
  role?: string | null,
): boolean => {
  const storedRole =
    role ?? (JSON.parse(localStorage.getItem('user') || '{}')?.role as string | undefined);

  if (!user) {
    return storedRole === 'admin' || storedRole === 'superAdmin';
  }

  if (storedRole === 'admin' || storedRole === 'superAdmin') {
    return user.adminManageAnalytics !== false;
  }

  return isSubClientAnalyticsOnly(user);
};

export const syncAdminPermissionsToLocalStorage = (
  user: Partial<UserDetails> | null | undefined,
): void => {
  if (!isValidUserDetailsRecord(user)) {
    return;
  }

  localStorage.setItem(
    ADMIN_PERMISSIONS_STORAGE_KEY,
    JSON.stringify(toAdminPermissionsSnapshot(user)),
  );
};

export const readAdminPermissionsFromLocalStorage =
  (): Partial<AdminPermissionsSnapshot> | null => {
    const stored = localStorage.getItem(ADMIN_PERMISSIONS_STORAGE_KEY);

    if (!stored) {
      return null;
    }

    try {
      return JSON.parse(stored) as Partial<AdminPermissionsSnapshot>;
    } catch {
      return null;
    }
  };

export const shouldShowSubClientAnalyticsNav = (
  role: string | undefined | null,
  userDetails?: Partial<AdminPermissionsSnapshot> | null | undefined,
): boolean => {
  if (role !== 'user') {
    return false;
  }

  if (
    coerceBoolean(userDetails?.adminManageSubClientAnalytics) &&
    !coerceBoolean(userDetails?.adminManageAnalytics)
  ) {
    return true;
  }

  const stored = readAdminPermissionsFromLocalStorage();

  return (
    coerceBoolean(stored?.adminManageSubClientAnalytics) &&
    !coerceBoolean(stored?.adminManageAnalytics)
  );
};

/** Prefer GET /user/:id fields; fall back to adminPermissions in localStorage. */
export const resolveAdminPermissionSource = (
  userDetails?: unknown,
): Partial<AdminPermissionsSnapshot> | null => {
  const stored = readAdminPermissionsFromLocalStorage();

  if (!isValidUserDetailsRecord(userDetails)) {
    return stored;
  }

  return {
    ...stored,
    ...toAdminPermissionsSnapshot(userDetails),
  };
};

export const canManageFreedomToSpeakUp = (
  user?: Partial<Pick<UserDetails, 'adminManageFreedomToSpeakUp'>> | null,
): boolean => coerceBoolean(user?.adminManageFreedomToSpeakUp);

export const canManageTrauma = (
  user?: Partial<Pick<UserDetails, 'adminManageTrauma'>> | null,
): boolean => coerceBoolean(user?.adminManageTrauma);

/**
 * Hotfix: employer Experts nav/dashboard until a dedicated experts-credits permission exists.
 * Hides Experts for trauma line-manager style admins (Manage trauma without View individual records),
 * unless they also have another platform manage permission.
 */
export const shouldShowEmployerExpertsNav = (
  expertsEnabled: boolean | undefined,
  permissions?: Partial<AdminPermissionsSnapshot> | null,
): boolean => {
  if (!expertsEnabled) {
    return false;
  }

  if (!permissions) {
    return true;
  }

  const isTraumaLineManagerStyle =
    coerceBoolean(permissions.adminManageTrauma) &&
    !coerceBoolean(permissions.adminViewTraumaIndividualRecords);

  if (!isTraumaLineManagerStyle) {
    return true;
  }

  return (
    coerceBoolean(permissions.adminManageUsers) ||
    coerceBoolean(permissions.adminManageAnalytics) ||
    coerceBoolean(permissions.adminManageCommentsAndSpeakUp) ||
    coerceBoolean(permissions.adminManageFreedomToSpeakUp) ||
    coerceBoolean(permissions.adminManageCommunity)
  );
};

export const canLogTraumaIncidents = (
  user?: Partial<Pick<UserDetails, 'adminManageTrauma' | 'adminLogTraumaIncidents'>> | null,
): boolean =>
  coerceBoolean(user?.adminManageTrauma) && coerceBoolean(user?.adminLogTraumaIncidents);

export const canViewTraumaIndividualRecords = (
  user?: Partial<
    Pick<UserDetails, 'adminManageTrauma' | 'adminViewTraumaIndividualRecords'>
  > | null,
): boolean =>
  coerceBoolean(user?.adminManageTrauma) && coerceBoolean(user?.adminViewTraumaIndividualRecords);

export const canRespondToTraumaMessages = (
  user?: Partial<
    Pick<
      UserDetails,
      'adminManageTrauma' | 'adminViewTraumaIndividualRecords' | 'adminRespondToTraumaMessages'
    >
  > | null,
): boolean =>
  coerceBoolean(user?.adminManageTrauma) &&
  coerceBoolean(user?.adminViewTraumaIndividualRecords) &&
  coerceBoolean(user?.adminRespondToTraumaMessages);

export const canUpdateTraumaOutcomes = (
  user?: Partial<
    Pick<
      UserDetails,
      'adminManageTrauma' | 'adminViewTraumaIndividualRecords' | 'adminUpdateTraumaOutcomes'
    >
  > | null,
): boolean =>
  coerceBoolean(user?.adminManageTrauma) &&
  coerceBoolean(user?.adminViewTraumaIndividualRecords) &&
  coerceBoolean(user?.adminUpdateTraumaOutcomes);

export const resolveEmployerTraumaLandingPath = (
  user?: Partial<
    Pick<UserDetails, 'adminManageTrauma' | 'adminViewTraumaIndividualRecords'>
  > | null,
): string =>
  canViewTraumaIndividualRecords(user)
    ? EMPLOYER_TRAUMA_STAFF_DASHBOARD_ROUTE
    : EMPLOYER_TRAUMA_INCIDENTS_ROUTE;

export const isEmployerTraumaNavPathActive = (pathname: string, menuPath: string): boolean => {
  if (
    menuPath === EMPLOYER_TRAUMA_STAFF_DASHBOARD_ROUTE ||
    menuPath === EMPLOYER_TRAUMA_INCIDENTS_ROUTE
  ) {
    return pathname.startsWith('/employer/trauma');
  }

  return pathname === menuPath;
};

export const normalizeUsersList = <T extends Record<string, unknown>>(users: T[]): T[] =>
  users.map((user) => normalizeUserApiRecord(user));

export const canManageCommentsAndSpeakUp = (
  user?: Partial<Pick<UserDetails, 'adminManageCommentsAndSpeakUp'>> | null,
): boolean => {
  if (!user || user.adminManageCommentsAndSpeakUp === undefined) {
    return true;
  }

  return coerceBoolean(user.adminManageCommentsAndSpeakUp);
};
